<template>
  <div>
    <div class="tw-my-[30px]">
      <div class="boxes" v-if="step == 0">
        <div
          v-for="(s, key) in syllables?.correct"
          :key="s.id"
          class="tw-inline-block tw-px-[5px]"
          :class="'act-dl-cl-' + (key + 1)"
        >
          {{ s.value }}
        </div>
      </div>
      <div v-if="step > 0">
        <div
          v-for="(s, key) in syllables?.correct"
          :key="s.id"
          class="tw-relative tw-top-0 tw-left-0 tw-inline-block tw-m-[10px]"
        >
          <a
            v-if="canGoToSyllable(key)"
            href=""
            class="tw-block tw-p-[10px] tw-border tw-border-solid act-dl-border 
              hover:tw-bg-primaryvlt tw-no-underline"
            :class="'act-dl-cl-' + (key + 1)"
            @click.prevent="selectSyllable(key)"
          >
            {{ getSyllableValue(key) }}
          </a>
          <span
            v-else
            class="tw-block tw-p-[10px] tw-border tw-border-solid act-dl-border"
            :class="'act-dl-cl-' + (key + 1)"
          >
            {{ getSyllableValue(key) }}
          </span>
          <div 
            v-if="optionResults.length > key" 
            class="tw-absolute tw-top-[-12px] tw-right-[-19px]"
          >
            <ResultMark :result="optionResults[key] > 0" />
          </div>
        </div>
      </div>
      <div v-if="step == 1">
        <div
          v-for="s in syllables?.options[currentSyllable]"
          :key="s.id"
          class="tw-inline-block tw-m-[10px]"
        >
          <a
            href=""
            @click.prevent="selectOption(s.value)"
            class="tw-block tw-p-[10px] tw-border tw-border-solid act-dl-border
              hover:tw-bg-primaryvlt tw-no-underline"
            :class="'act-dl-cl-' + (currentSyllable + 1)"
          >
            {{ s.value }}
          </a>
        </div>
      </div>
    </div>
    <div v-if="step == 0">
      <button class="sf-btn-primary" @click="begin">Begin</button>
    </div>
    <div v-if="step == 2">
      <button class="sf-btn-primary" @click="check">Check</button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { SfSortedSyllables } from "@/types/sfActivityTypes";
import {
  sfActivityCurrentWord,
  sfPlayActivityAudio,
  sfStopActivityAudio,
} from "@/state/sfActivityState";
import ResultMark from "./ResultMark.vue";

export default defineComponent({
  components: { ResultMark },
  emits: ["success"],
  data() {
    return {
      step: 0,
      currentSyllable: -1,
      checkSyllable: 0,
      optionValues: [] as Array<string | null>,
      optionResults: [] as number[],
    };
  },
  computed: {
    syllables(): SfSortedSyllables | null {
      if (!sfActivityCurrentWord.value) {
        return null;
      }
      return sfActivityCurrentWord.value.sortedSyllables;
    },
    syllableCount(): number {
      if (!this.syllables) {
        return 0;
      }
      return this.syllables.correct.length;
    },
  },
  watch: {
    currentSyllable(a: number, b: number) {
      if (a != b && a >= 0 && a < this.syllableCount) {
        sfPlayActivityAudio("syl_" + this.syllables?.correct[a].id);
      }
    },
  },
  methods: {
    begin() {
      sfStopActivityAudio();
      this.step = 1;
      this.currentSyllable = 0;
      this.checkSyllable = -1;
    },
    check() {
      this.step = 3;
      this.checkSyllable = 0;
      this.checkNextSyllable();
    },
    getSyllableValue(n: number) {
      if (n == this.currentSyllable) {
        return "?";
      }
      if (this.optionValues[n]) {
        return this.optionValues[n];
      }
      return "_";
    },
    selectSyllable(syllableId: number) {
      this.currentSyllable = syllableId;
      this.step = 1;
    },
    selectOption(value: string) {
      let values = [...this.optionValues];
      values[this.currentSyllable] = value;
      this.optionValues = values;
      this.goToNextSyllable();
    },
    canGoToSyllable(n: number) {
      if (this.step == 3) {
        return false;
      }
      if (n == this.currentSyllable) {
        return false;
      }
      if (!this.optionValues[n]) {
        return false;
      }
      return true;
    },
    goToNextSyllable() {
      if (!this.syllables) {
        return;
      }
      for (let i = 0; i < 100; i++) {
        if (!this.optionValues[i]) {
          if (i >= this.syllables.correct.length) {
            this.step = 2;
            this.currentSyllable = -1;
          } else {
            this.currentSyllable = i;
          }
          break;
        }
      }
    },
    checkNextSyllable() {
      if (!this.syllables) {
        return;
      }
      let results = [...this.optionResults];
      let sId = this.checkSyllable;
      if (sId >= this.syllableCount) {
        // check if there were errors
        let isCorrect = true;
        let values = [...this.optionValues];
        for (let i in this.optionResults) {
          if (this.optionResults[i] < 0) {
            isCorrect = false;
            values[i] = null;
          }
        }
        if (isCorrect) {
          this.$emit("success");
        } else {
          this.step = 1;
          this.optionValues = values;
          this.optionResults = [];
          this.goToNextSyllable();
        }
        return;
      }
      results[sId] =
        this.optionValues[sId] == this.syllables.correct[sId].value ? 1 : -1;
      sfPlayActivityAudio("syl_" + this.syllables.correct[sId].id);
      this.optionResults = results;
      this.checkSyllable = sId + 1;
      setTimeout(this.checkNextSyllable, 1000);
    },
  },
});
</script>
